// src/features/etf-info/components/AllocationCharts.tsx
//
// Component Island (Island 4): two donut charts side-by-side showing the
// ETF's sector/industry exposure and its geographic (country) exposure.
// Each island fetches independently so one failing endpoint doesn't block
// the other. The backend already coerces the FMP country "97.32%" string
// into a clean float.

import { useEffect, useMemo, useState } from 'react';
import {
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';

import {
    fetchCountryWeightings,
    fetchSectorWeightings,
    type CountryWeight,
    type SectorWeight,
} from '../api';

// Categorical palette (colour-blind-friendly-ish) reused across both donuts.
const PALETTE = [
    '#3b82f6',
    '#8b5cf6',
    '#ec4899',
    '#f59e0b',
    '#10b981',
    '#06b6d4',
    '#ef4444',
    '#84cc16',
    '#a855f7',
    '#14b8a6',
    '#f97316',
    '#64748b',
];

interface Slice {
    name: string;
    value: number;
}

const DonutChart = ({
    title,
    subtitle,
    slices,
    isLoading,
}: {
    title: string;
    subtitle: string;
    slices: Slice[];
    isLoading: boolean;
}) => {
    return (
        <div className="surface-card h-full flex flex-col">
            <h2 className="section-title mb-1">{title}</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">{subtitle}</p>

            <div className="relative flex-1 min-h-[300px]">
                {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-500 dark:text-slate-400 animate-pulse text-sm">
                        Loading exposure…
                    </div>
                ) : slices.length === 0 ? (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm">
                        No exposure data available.
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={slices}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius="55%"
                                outerRadius="80%"
                                paddingAngle={1}
                                isAnimationActive={false}
                            >
                                {slices.map((_, idx) => (
                                    <Cell
                                        key={idx}
                                        fill={PALETTE[idx % PALETTE.length]}
                                    />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#0f172a',
                                    border: '1px solid #334155',
                                    borderRadius: '8px',
                                    color: '#e2e8f0',
                                }}
                                labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                                itemStyle={{ color: '#e2e8f0' }}
                                formatter={(value: number, name: string) => [
                                    `${Number(value).toFixed(2)}%`,
                                    name,
                                ]}
                            />
                            <Legend
                                verticalAlign="bottom"
                                iconType="circle"
                                wrapperStyle={{ fontSize: 11 }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export const AllocationCharts = ({ symbol }: { symbol: string }) => {
    const [sectors, setSectors] = useState<SectorWeight[]>([]);
    const [countries, setCountries] = useState<CountryWeight[]>([]);
    const [loadingSectors, setLoadingSectors] = useState(true);
    const [loadingCountries, setLoadingCountries] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoadingSectors(true);
        setLoadingCountries(true);

        fetchSectorWeightings(symbol)
            .then((res) => {
                if (!cancelled) setSectors(res);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                if (!cancelled) setSectors([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingSectors(false);
            });

        fetchCountryWeightings(symbol)
            .then((res) => {
                if (!cancelled) setCountries(res);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                if (!cancelled) setCountries([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingCountries(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    const sectorSlices = useMemo<Slice[]>(
        () =>
            sectors
                .filter((s) => s.sector && (s.weight_percentage ?? 0) > 0)
                .map((s) => ({
                    name: s.sector as string,
                    value: s.weight_percentage as number,
                }))
                .sort((a, b) => b.value - a.value),
        [sectors],
    );

    const countrySlices = useMemo<Slice[]>(
        () =>
            countries
                .filter((c) => c.country && (c.weight_percentage ?? 0) > 0)
                .map((c) => ({
                    name: c.country as string,
                    value: c.weight_percentage as number,
                }))
                .sort((a, b) => b.value - a.value),
        [countries],
    );

    return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
            <DonutChart
                title="Sector Exposure"
                subtitle="Industry allocation across the underlying basket"
                slices={sectorSlices}
                isLoading={loadingSectors}
            />
            <DonutChart
                title="Geographic Exposure"
                subtitle="Country allocation across the underlying basket"
                slices={countrySlices}
                isLoading={loadingCountries}
            />
        </div>
    );
};

export default AllocationCharts;
